import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createEvenement,
  deleteEvenement,
  getAgenda,
  marquerFait,
  updateEvenement,
} from '../services/agendaService';
import { getFormateurs } from '../services/formateurService';
import SelectRecherche from '../components/SelectRecherche';
import { anneeCourante, anneeCouranteDesChamps } from '../utils/anneeCourante';
import { FORMATS_FORMATION, LIBELLES_LIEN, estDistanciel, estLienTeams } from '../utils/formatsFormation';
import '../assets/styles/Agenda.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import RemplirParTexte from '../components/RemplirParTexte';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/** Nature d'un évènement saisi : elle décide de la couleur de la pastille. */
const CATEGORIES = [
  { value: 'tache', label: 'À faire (tâche)' },
  // Une formation prévue, notée avant d'être vendue. Les formations vendues
  // viennent des contrats : elles s'ajoutent toutes seules.
  { value: 'formation', label: 'Formation' },
  { value: 'formation_prevue', label: 'Formation prévue' },
  { value: 'rendez_vous', label: 'Rendez-vous' },
  { value: 'rappel', label: 'Rappel' },
  { value: 'absence', label: 'Absence' },
  { value: 'autre', label: 'Autre' },
];

const EVENEMENT_VIDE = {
  titre: '',
  description: '',
  dateDebut: '',
  dateFin: '',
  heureDebut: '',
  heureFin: '',
  lieu: '',
  categorie: 'tache',
  aFaire: true,
  liens: [],
  // Nature « formation » : format, formateur (fiche ou saisie) et responsable.
  format: '',
  formateurId: '',
  formateurNom: '',
  formateurEmail: '',
  responsableNom: '',
  responsableEmail: '',
};

/** Une ligne de lien vierge : libellé facultatif, URL obligatoire. */
const lienVide = () => ({ libelle: '', url: '' });

/**
 * Valeurs proposées par l'IA : un <input type="time"> n'accepte que « hh:mm »,
 * on remet donc « 9:30 » ou « 09:30:00 » à ce format et on écarte le reste
 * plutôt que de poser une heure que le champ afficherait vide.
 */
function valeursEvenement(v) {
  const sortie = { ...v };
  for (const nom of ['heureDebut', 'heureFin']) {
    if (!(nom in sortie)) continue;
    const m = /^(\d{1,2}):(\d{2})/.exec(String(sortie[nom]).trim());
    if (m && Number(m[1]) < 24 && Number(m[2]) < 60) sortie[nom] = `${m[1].padStart(2, '0')}:${m[2]}`;
    else delete sortie[nom];
  }
  return sortie;
}

/**
 * Seuls http et https s'affichent en lien cliquable. Le serveur refuse déjà
 * le reste (cf. agendaService), mais un évènement enregistré avant cette
 * règle pourrait en porter : mieux vaut ne pas le rendre cliquable que de
 * poser un « javascript: » dans un href.
 */
const lienSur = (url) => /^https?:\/\//i.test(String(url || ''));

/* ------------------------------------------------------------------ */
/* Dates — tout se manipule en 'aaaa-mm-jj', jamais en Date            */
/* ------------------------------------------------------------------ */

/*
 * Les bornes viennent du serveur en 'aaaa-mm-jj' et y retournent telles
 * quelles. Passer par des `Date` ferait entrer le fuseau dans le calcul : à
 * Paris, `new Date('2026-09-01')` est le 31 août à 22 h, et la grille
 * décalerait d'un jour la moitié de l'année.
 */
const cle = (annee, mois, jour) =>
  `${annee}-${String(mois + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;

function aujourdhui() {
  const d = new Date();
  return cle(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Lundi = 0 : la semaine française commence là où la grille commence. */
const indiceJour = (annee, mois, jour) => (new Date(annee, mois, jour).getDay() + 6) % 7;

const nbJours = (annee, mois) => new Date(annee, mois + 1, 0).getDate();

/** « Lun », « Mar »… d'une date 'aaaa-mm-jj' : la liste des journées, sur téléphone. */
function jourSemaine(iso) {
  const [a, m, j] = iso.split('-').map(Number);
  return JOURS[indiceJour(a, m - 1, j)];
}

/** Les six semaines affichées, du lundi précédant le 1er au dimanche final. */
function grilleDuMois(annee, mois) {
  const decalage = indiceJour(annee, mois, 1);
  const total = nbJours(annee, mois);
  const precedent = nbJours(annee, mois === 0 ? 11 : mois - 1);

  const cases = [];
  for (let i = 0; i < decalage; i += 1) {
    const jour = precedent - decalage + 1 + i;
    const m = mois === 0 ? 11 : mois - 1;
    const a = mois === 0 ? annee - 1 : annee;
    cases.push({ date: cle(a, m, jour), jour, horsMois: true });
  }
  for (let jour = 1; jour <= total; jour += 1) {
    cases.push({ date: cle(annee, mois, jour), jour, horsMois: false });
  }
  // Toujours des semaines entières, et six rangées : la grille garde la même
  // hauteur d'un mois à l'autre, sans sauter sous le curseur.
  let suivant = 1;
  while (cases.length % 7 !== 0 || cases.length < 42) {
    const m = mois === 11 ? 0 : mois + 1;
    const a = mois === 11 ? annee + 1 : annee;
    cases.push({ date: cle(a, m, suivant), jour: suivant, horsMois: true });
    suivant += 1;
  }
  return cases;
}

const formatJourLong = (iso) => {
  const [a, m, j] = iso.split('-').map(Number);
  return `${j} ${MOIS[m - 1]} ${a}`;
};

/** Un évènement peut durer : il se pose sur chaque journée qu'il couvre. */
function indexerParJour(entrees) {
  const parJour = new Map();
  for (const e of entrees) {
    let jour = e.debut;
    // Boucle sur les dates en incrémentant par Date locale puis re-sérialisé :
    // sûr sur les changements de mois comme sur l'heure d'été.
    while (jour <= e.fin) {
      if (!parJour.has(jour)) parJour.set(jour, []);
      parJour.get(jour).push(e);
      const [a, m, j] = jour.split('-').map(Number);
      const suivant = new Date(a, m - 1, j + 1);
      jour = cle(suivant.getFullYear(), suivant.getMonth(), suivant.getDate());
    }
  }
  for (const liste of parJour.values()) {
    liste.sort((x, y) => (x.heureDebut || '99').localeCompare(y.heureDebut || '99'));
  }
  return parJour;
}

/** Les deux natures qui portent format, formateur et responsable du dossier. */
const estFormation = (categorie) => categorie === 'formation' || categorie === 'formation_prevue';

/** Classe de couleur d'une entrée, selon sa source puis sa catégorie. */
function classeEntree(e) {
  if (e.source === 'formation') return 'agenda-ev-formation';
  if (e.source === 'catalogue') return 'agenda-ev-catalogue';
  // Terminé prime sur la catégorie : c'est ce qu'on cherche à voir d'abord.
  if (e.faitLe) return 'agenda-ev-fait';
  // « agenda-ev-formation » est la couleur des formations vendues : celle d'une
  // « Formation » saisie à la main. Une formation prévue a la sienne.
  const nature = e.categorie === 'formation_prevue' ? 'prevue' : e.categorie;
  if (e.aFaire) return `agenda-ev-${nature} agenda-ev-afaire`;
  return `agenda-ev-${nature}`;
}

/* ------------------------------------------------------------------ */
/* La page                                                             */
/* ------------------------------------------------------------------ */

/**
 * Agenda mensuel : les formations vendues, les sessions en inter et les
 * évènements saisis à la main, sur une même grille.
 *
 * Les trois sources ne se ressemblent pas : une formation et une session du
 * catalogue se lisent (elles se modifient depuis leur écran), un évènement
 * s'ajoute, se coche et se supprime ici.
 */
function Agenda() {
  const navigate = useNavigate();
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  const [mois, setMois] = useState(maintenant.getMonth());

  const [entrees, setEntrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [jourOuvert, setJourOuvert] = useState(null);
  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(EVENEMENT_VIDE);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });
  const [formError, setFormError] = useState(null);
  // Fiches des formateurs actifs, pour le choix du formateur d'une formation.
  const [formateurs, setFormateurs] = useState([]);
  useEffect(() => {
    if (!edition) return undefined;
    let vivant = true;
    getFormateurs({ actif: true })
      .then((res) => vivant && setFormateurs(Array.isArray(res.data) ? res.data : []))
      .catch(() => vivant && setFormateurs([]));
    return () => {
      vivant = false;
    };
  }, [edition]);

  const cases = useMemo(() => grilleDuMois(annee, mois), [annee, mois]);
  const debut = cases[0].date;
  const fin = cases[cases.length - 1].date;

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getAgenda({ debut, fin })
      .then((res) => setEntrees(res.data || []))
      .catch((err) => setError(err.message || "Impossible de charger l'agenda."))
      .finally(() => setLoading(false));
  }, [debut, fin]);

  useEffect(() => {
    charger();
  }, [charger]);

  const parJour = useMemo(() => indexerParJour(entrees), [entrees]);
  const jour = aujourdhui();

  function allerA(deltaMois) {
    const d = new Date(annee, mois + deltaMois, 1);
    setAnnee(d.getFullYear());
    setMois(d.getMonth());
  }

  function ouvrirCreation(date) {
    setEdition({ id: null });
    setForm({ ...EVENEMENT_VIDE, dateDebut: date, dateFin: date });
    setFormError(null);
  }

  function ouvrirEdition(e) {
    setEdition({ id: e.id });
    setForm({
      titre: e.titre ?? '',
      description: e.description ?? '',
      dateDebut: e.debut ?? '',
      dateFin: e.fin ?? '',
      heureDebut: e.heureDebut ?? '',
      heureFin: e.heureFin ?? '',
      lieu: e.lieu ?? '',
      categorie: e.categorie ?? 'autre',
      aFaire: e.aFaire ?? false,
      liens: Array.isArray(e.liens) ? e.liens.map((l) => ({ ...l })) : [],
      format: e.format ?? '',
      formateurId: e.formateurId ?? '',
      formateurNom: e.formateurNom ?? '',
      formateurEmail: e.formateurEmail ?? '',
      responsableNom: e.responsableNom ?? '',
      responsableEmail: e.responsableEmail ?? '',
    });
    setFormError(null);
  }

  const champ = (nom) => (ev) =>
    setForm((prec) => ({
      ...prec,
      [nom]: ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value,
    }));

  /**
   * À la création, une année passée (13/02/2023) est ramenée à l'année en
   * cours (13/02/2026). En modification on ne touche à rien : on ne réécrit
   * pas la date d'un évènement déjà posé.
   */
  const champDate = (nom) => (ev) => {
    const valeur = edition?.id === null ? anneeCourante(ev.target.value) : ev.target.value;
    setForm((prec) => {
      const suite = { ...prec, [nom]: valeur };
      // Un évènement d'une seule journée garde sa fin sur son début : changer
      // la date de début ne doit pas l'étirer jusqu'à l'ancienne date.
      if (nom === 'dateDebut' && (prec.dateFin === prec.dateDebut || (prec.dateFin && prec.dateFin < valeur))) {
        suite.dateFin = valeur;
      }
      return suite;
    });
  };

  /** Le type d'évènement décide du rappel par défaut : une tâche se réclame, une formation prévue non. */
  function changerNature(ev) {
    const categorie = ev.target.value;
    setForm((prec) => ({
      ...prec,
      categorie,
      aFaire: edition?.id === null ? categorie === 'tache' : prec.aFaire,
    }));
  }

  /** Choisir un formateur remplit son nom et son email ; les modifier à la main le détache de sa fiche. */
  function choisirFormateur(ev) {
    const id = ev.target.value;
    const f = formateurs.find((x) => String(x.id) === String(id));
    setForm((prec) => ({
      ...prec,
      formateurId: f ? f.id : '',
      formateurNom: f ? [f.prenom, f.nom].filter(Boolean).join(' ') : prec.formateurNom,
      formateurEmail: f ? f.email || '' : prec.formateurEmail,
    }));
  }

  const champFormateur = (nom) => (ev) =>
    setForm((prec) => ({ ...prec, [nom]: ev.target.value, formateurId: '' }));

  // Formation à distance sans lien Teams : le formulaire le dit avant l'enregistrement.
  const teamsManquant =
    estFormation(form.categorie) && estDistanciel(form.format) && !form.liens.some(estLienTeams);

  async function enregistrer(ev) {
    ev.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (edition.id === null) {
        await createEvenement(anneeCouranteDesChamps(form, ['dateDebut', 'dateFin']));
      } else {
        await updateEvenement(edition.id, form);
      }
      setEdition(null);
      await charger();
    } catch (err) {
      setFormError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  async function basculerFait(e) {
    try {
      await marquerFait(e.id, !e.faitLe);
      await charger();
    } catch (err) {
      setError(err.message || 'Modification impossible.');
    }
  }

  async function supprimer(e) {
    try {
      await deleteEvenement(e.id);
      setEdition(null);
      await charger();
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
    }
  }

  /**
   * Ouvrir une entrée : modifier ce qu'on a saisi, ou lire le détail d'une
   * session de formation — ses feuilles de présence, participants et
   * formateurs.
   */
  function ouvrirEntree(e) {
    if (e.source === 'evenement') ouvrirEdition(e);
    else navigate(`/calendrier/session/${e.source}/${e.id}`);
  }

  const duJour = jourOuvert ? parJour.get(jourOuvert) || [] : [];

  return (
    <div className="agenda">
      <div className="agenda-barre">
        <div className="agenda-navigation">
          <button type="button" className="agenda-fleche" onClick={() => allerA(-1)} aria-label="Mois précédent">
            ‹
          </button>
          <div className="agenda-mois">
            {MOIS[mois]} {annee}
          </div>
          <button type="button" className="agenda-fleche" onClick={() => allerA(1)} aria-label="Mois suivant">
            ›
          </button>
          <button
            type="button"
            className="cal-btn cal-btn-sm"
            onClick={() => {
              const d = new Date();
              setAnnee(d.getFullYear());
              setMois(d.getMonth());
            }}
          >
            Aujourd&apos;hui
          </button>
        </div>

        <div className="agenda-legende">
          <span className="agenda-puce agenda-ev-formation" /> Formation vendue
          <span className="agenda-puce agenda-ev-prevue" /> Formation prévue
          <span className="agenda-puce agenda-ev-catalogue" /> Session en inter
          <span className="agenda-puce agenda-ev-tache" /> À faire
          <button type="button" className="cal-btn cal-btn-primary" onClick={() => ouvrirCreation(jour)}>
            + Évènement
          </button>
        </div>
      </div>

      {error && <div className="agenda-message agenda-message-erreur">{error}</div>}

      <div className={'agenda-grille' + (loading ? ' agenda-grille-chargement' : '')}>
        <div className="agenda-entetes">
          {JOURS.map((j) => (
            <div className="agenda-entete" key={j}>
              {j}
            </div>
          ))}
        </div>

        <div className="agenda-cases">
          {cases.map((c) => {
            const liste = parJour.get(c.date) || [];
            return (
              <div
                key={c.date}
                className={
                  'agenda-case' +
                  (c.horsMois ? ' agenda-case-hors' : '') +
                  (c.date === jour ? ' agenda-case-aujourdhui' : '')
                }
                onDoubleClick={() => ouvrirCreation(c.date)}
              >
                <div className="agenda-case-tete">
                  <span className="agenda-numero" data-jour={jourSemaine(c.date)}>
                    {c.jour}
                  </span>
                  <button
                    type="button"
                    className="agenda-ajout"
                    onClick={() => ouvrirCreation(c.date)}
                    aria-label={`Ajouter un évènement le ${formatJourLong(c.date)}`}
                    title="Ajouter un évènement"
                  >
                    +
                  </button>
                </div>

                <div className="agenda-case-corps">
                  {liste.slice(0, 3).map((e) => (
                    <div
                      key={e.cle}
                      className={`agenda-ev ${classeEntree(e)}`}
                      title={[e.titre, e.detail, e.lieu].filter(Boolean).join(' — ')}
                    >
                      {/* Se coche depuis la grille : c'est le geste de fin de
                          journée, il ne mérite pas d'ouvrir une fiche. */}
                      {e.source === 'evenement' && (
                        <button
                          type="button"
                          className="agenda-coche"
                          role="checkbox"
                          aria-checked={Boolean(e.faitLe)}
                          onClick={() => basculerFait(e)}
                          title={e.faitLe ? 'Marquer comme à refaire' : 'Marquer comme terminé'}
                        >
                          {e.faitLe ? '✓' : ''}
                        </button>
                      )}
                      <button
                        type="button"
                        className="agenda-ev-ouvrir"
                        onClick={() => ouvrirEntree(e)}
                      >
                        {e.heureDebut && <span className="agenda-ev-heure">{e.heureDebut}</span>}
                        <span className="agenda-ev-titre">{e.titre}</span>
                        {e.lienTeamsManquant && (
                          <span className="agenda-ev-alerte" title="Formation à distance : aucun lien Teams renseigné">
                            ⚠
                          </span>
                        )}
                      </button>
                    </div>
                  ))}
                  {liste.length > 3 && (
                    <button
                      type="button"
                      className="agenda-plus"
                      onClick={() => setJourOuvert(c.date)}
                    >
                      +{liste.length - 3} autre{liste.length - 3 > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------- Le détail d'une journée ---------- */}
      {jourOuvert && (
        <div className="cal-modal-overlay" onClick={() => setJourOuvert(null)}>
          <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h2 className="cal-modal-title">{formatJourLong(jourOuvert)}</h2>
              <button type="button" className="cal-modal-close" onClick={() => setJourOuvert(null)}>
                ×
              </button>
            </div>
            <div className="cal-modal-body">
              {duJour.length === 0 && <p className="agenda-vide">Rien de prévu ce jour-là.</p>}
              <ul className="agenda-liste">
                {duJour.map((e) => (
                  <li key={e.cle} className="agenda-liste-item">
                    <span className={`agenda-puce ${classeEntree(e)}`} />
                    <div className="agenda-liste-texte">
                      <div className={'agenda-liste-titre' + (e.faitLe ? ' agenda-liste-fait' : '')}>
                        {e.titre}
                      </div>
                      <div className="agenda-liste-detail">
                        {[
                          e.heureDebut && `${e.heureDebut}${e.heureFin ? ` – ${e.heureFin}` : ''}`,
                          e.detail,
                          e.lieu,
                          e.reference,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      {e.source === 'evenement' && estFormation(e.categorie) && (
                        <div className="agenda-liste-detail">
                          {[
                            e.format && (FORMATS_FORMATION.find((f) => f.value === e.format)?.label || e.format),
                            e.formateurNom && `Formateur : ${e.formateurNom}${e.formateurEmail ? ` (${e.formateurEmail})` : ''}`,
                            e.responsableNom &&
                              `Responsable : ${e.responsableNom}${e.responsableEmail ? ` (${e.responsableEmail})` : ''}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                      {e.lienTeamsManquant && (
                        <div className="agenda-alerte-ligne">
                          ⚠ Formation à distance : aucun lien Teams renseigné.
                        </div>
                      )}
                      {e.description && <div className="agenda-liste-detail">{e.description}</div>}
                      {(e.liens || []).length > 0 && (
                        <div className="agenda-liste-liens">
                          {e.liens.map((l, i) =>
                            lienSur(l.url) ? (
                              <a
                                key={i}
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="agenda-lien"
                                title={l.url}
                              >
                                {l.libelle || l.url}
                              </a>
                            ) : (
                              <span className="agenda-lien agenda-lien-inerte" key={i}>
                                {l.libelle || l.url}
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    {e.source !== 'evenement' && (
                      <div className="agenda-liste-actions">
                        <button
                          type="button"
                          className="cal-btn cal-btn-sm cal-btn-primary"
                          onClick={() => ouvrirEntree(e)}
                        >
                          Détails
                        </button>
                      </div>
                    )}
                    {e.source === 'evenement' && (
                      <div className="agenda-liste-actions">
                        <button
                          type="button"
                          className={'cal-btn cal-btn-sm' + (e.faitLe ? '' : ' cal-btn-primary')}
                          onClick={() => basculerFait(e)}
                        >
                          {e.faitLe ? 'À refaire' : 'Marquer fait'}
                        </button>
                        <button
                          type="button"
                          className="cal-btn cal-btn-sm"
                          onClick={() => {
                            setJourOuvert(null);
                            ouvrirEdition(e);
                          }}
                        >
                          Modifier
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <div className="cal-form-actions">
                <button
                  type="button"
                  className="cal-btn cal-btn-primary"
                  onClick={() => {
                    const date = jourOuvert;
                    setJourOuvert(null);
                    ouvrirCreation(date);
                  }}
                >
                  + Ajouter ce jour-là
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Créer ou modifier un évènement ---------- */}
      {edition && (
        <div className="cal-modal-overlay" onClick={() => setEdition(null)}>
          <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h2 className="cal-modal-title">
                {edition.id === null ? 'Nouvel évènement' : "Modifier l'évènement"}
              </h2>
              <button type="button" className="cal-modal-close" onClick={() => setEdition(null)}>
                ×
              </button>
            </div>

            <form className="cal-form cal-modal-body" onSubmit={enregistrer}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && <div className="cal-form-message cal-form-message-error">{formError}</div>}

              {/* À la création comme à la modification : le mail ou le message collé suffit à remplir la fiche. */}
              <RemplirParTexte
                contexte="évènement de l'agenda (tâche, rendez-vous, rappel ou absence)"
                champs={[
                  { cle: 'titre', libelle: 'Titre' },
                  { cle: 'dateDebut', libelle: 'Du', type: 'date' },
                  { cle: 'dateFin', libelle: 'Au', type: 'date' },
                  { cle: 'heureDebut', libelle: 'Heure de début (hh:mm, 24 h)' },
                  { cle: 'heureFin', libelle: 'Heure de fin (hh:mm, 24 h)' },
                  {
                    cle: 'categorie',
                    libelle: 'Nature',
                    type: 'choix',
                    options: CATEGORIES.map((c) => ({ valeur: c.value, libelle: c.label })),
                  },
                  {
                    cle: 'format',
                    libelle: 'Format de la formation',
                    type: 'choix',
                    options: FORMATS_FORMATION.map((f) => ({ valeur: f.value, libelle: f.label })),
                  },
                  { cle: 'formateurNom', libelle: 'Nom du formateur' },
                  { cle: 'formateurEmail', libelle: 'Email du formateur' },
                  { cle: 'responsableNom', libelle: 'Nom du responsable du dossier' },
                  { cle: 'responsableEmail', libelle: 'Email du responsable du dossier' },
                  { cle: 'lieu', libelle: 'Lieu' },
                  { cle: 'description', libelle: 'Note' },
                ]}
                onValeurs={(v) =>
                  setForm((prec) => {
                    const valeurs = valeursEvenement(v);
                    return {
                      ...prec,
                      ...(edition.id === null ? anneeCouranteDesChamps(valeurs, ['dateDebut', 'dateFin']) : valeurs),
                    };
                  })
                }
              />

              <label className="cal-field">
                <span className="cal-label">Titre</span>
                <input
                  type="text"
                  className="cal-input"
                  value={form.titre}
                  onChange={champ('titre')}
                  maxLength={200}
                  placeholder="Rappeler le CSE, réunion de cadrage…"
                  required
                />
              </label>

              <div className="cal-form-row">
                <label className="cal-field">
                  <span className="cal-label">Du</span>
                  <input
                    type="date"
                    className="cal-input"
                    value={form.dateDebut}
                    onChange={champDate('dateDebut')}
                    required
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Au</span>
                  <input
                    type="date"
                    className="cal-input"
                    value={form.dateFin}
                    onChange={champDate('dateFin')}
                    min={form.dateDebut || undefined}
                  />
                </label>
              </div>

              <div className="cal-form-row">
                <label className="cal-field">
                  <span className="cal-label">Heure de début</span>
                  <input
                    type="time"
                    className="cal-input"
                    value={form.heureDebut}
                    onChange={champ('heureDebut')}
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Heure de fin</span>
                  <input
                    type="time"
                    className="cal-input"
                    value={form.heureFin}
                    onChange={champ('heureFin')}
                  />
                </label>
              </div>

              <div className="cal-form-row">
                <label className="cal-field">
                  <span className="cal-label">Nature</span>
                  <SelectRecherche className="cal-select" value={form.categorie} onChange={changerNature}>
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
                <label className="cal-field">
                  <span className="cal-label">Lieu</span>
                  <input
                    type="text"
                    className="cal-input"
                    value={form.lieu}
                    onChange={champ('lieu')}
                    maxLength={255}
                  />
                </label>
              </div>

              {estFormation(form.categorie) && (
                <div className="agenda-bloc-formation">
                  <div className="cal-form-section">Formation</div>

                  <label className="cal-field">
                    <span className="cal-label">Format</span>
                    <SelectRecherche className="cal-select" value={form.format} onChange={champ('format')}>
                      <option value="">— Choisir un format —</option>
                      {FORMATS_FORMATION.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </SelectRecherche>
                  </label>

                  <label className="cal-field">
                    <span className="cal-label">Formateur</span>
                    <SelectRecherche
                      className="cal-select"
                      value={form.formateurId ? String(form.formateurId) : ''}
                      onChange={choisirFormateur}
                    >
                      <option value="">— Saisie libre (nom et email ci-dessous) —</option>
                      {formateurs.map((f) => (
                        <option key={f.id} value={String(f.id)}>
                          {[f.prenom, f.nom].filter(Boolean).join(' ')}
                        </option>
                      ))}
                    </SelectRecherche>
                  </label>
                  <div className="cal-form-row">
                    <label className="cal-field">
                      <span className="cal-label">Nom du formateur</span>
                      <input
                        type="text"
                        className="cal-input"
                        value={form.formateurNom}
                        onChange={champFormateur('formateurNom')}
                        maxLength={200}
                      />
                    </label>
                    <label className="cal-field">
                      <span className="cal-label">Email du formateur</span>
                      <input
                        type="email"
                        className="cal-input"
                        value={form.formateurEmail}
                        onChange={champFormateur('formateurEmail')}
                        maxLength={255}
                      />
                    </label>
                  </div>

                  <div className="cal-form-row">
                    <label className="cal-field">
                      <span className="cal-label">Responsable du dossier</span>
                      <input
                        type="text"
                        className="cal-input"
                        value={form.responsableNom}
                        onChange={champ('responsableNom')}
                        maxLength={200}
                        placeholder="Nom"
                      />
                    </label>
                    <label className="cal-field">
                      <span className="cal-label">Email du responsable</span>
                      <input
                        type="email"
                        className="cal-input"
                        value={form.responsableEmail}
                        onChange={champ('responsableEmail')}
                        maxLength={255}
                      />
                    </label>
                  </div>
                </div>
              )}

              <label className="cal-field">
                <span className="cal-label">Note</span>
                <textarea
                  className="cal-textarea"
                  rows={3}
                  value={form.description}
                  onChange={champ('description')}
                />
              </label>

              <div className="cal-field">
                <span className="cal-label">Liens</span>
                <div className="agenda-liens-edition">
                  {form.liens.map((l, i) => (
                    <div className="agenda-lien-ligne" key={i}>
                      <input
                        type="text"
                        className="cal-input agenda-lien-libelle"
                        value={l.libelle || ''}
                        placeholder="Libellé : choisir ou saisir"
                        maxLength={100}
                        list="agenda-libelles-lien"
                        onChange={(e) =>
                          setForm((prec) => ({
                            ...prec,
                            liens: prec.liens.map((x, j) =>
                              j === i ? { ...x, libelle: e.target.value } : x
                            ),
                          }))
                        }
                      />
                      <input
                        type="url"
                        className="cal-input"
                        value={l.url || ''}
                        placeholder="https://…"
                        onChange={(e) =>
                          setForm((prec) => ({
                            ...prec,
                            liens: prec.liens.map((x, j) =>
                              j === i ? { ...x, url: e.target.value } : x
                            ),
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="agenda-lien-retirer"
                        onClick={() =>
                          setForm((prec) => ({
                            ...prec,
                            liens: prec.liens.filter((_, j) => j !== i),
                          }))
                        }
                        aria-label={`Retirer le lien ${i + 1}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="cal-btn cal-btn-sm"
                    onClick={() =>
                      setForm((prec) => ({ ...prec, liens: [...prec.liens, lienVide()] }))
                    }
                  >
                    + Ajouter un lien
                  </button>
                  {/* Le libellé se choisit dans cette liste ou se tape librement. */}
                  <datalist id="agenda-libelles-lien">
                    {LIBELLES_LIEN.map((l) => (
                      <option key={l} value={l} />
                    ))}
                  </datalist>
                </div>
              </div>

              {teamsManquant && (
                <div className="agenda-alerte" role="alert">
                  <span>
                    ⚠ Formation à distance : aucun lien Teams n’est renseigné. Ajoutez-le pour que le
                    formateur et les participants puissent se connecter.
                  </span>
                  <button
                    type="button"
                    className="cal-btn cal-btn-sm"
                    onClick={() =>
                      setForm((prec) => ({
                        ...prec,
                        liens: [...prec.liens, { libelle: 'Lien Teams', url: '' }],
                      }))
                    }
                  >
                    + Ajouter un lien Teams
                  </button>
                </div>
              )}

              <label className="cal-checkbox-field">
                <input type="checkbox" checked={form.aFaire} onChange={champ('aFaire')} />
                <span>
                  Me le rappeler (pastille rouge du Calendrier tant qu’il n’est pas coché)
                </span>
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement de l’évènement" estimation />
              <div className="cal-form-actions">
                {edition.id !== null && (
                  <button
                    type="button"
                    className="cal-btn cal-btn-danger"
                    onClick={() => supprimer({ id: edition.id })}
                    disabled={saving}
                  >
                    Supprimer
                  </button>
                )}
                <button
                  type="button"
                  className="cal-btn"
                  onClick={() => setEdition(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button type="submit" className="cal-btn cal-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Ajouter' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agenda;
